import React, { useState } from 'react'
import { reactular } from '@vendhq/reactular'
import { Card } from '@lightspeed/design-system-react'
import { ApiCustomer, ApiRetailerPaymentType } from '@vend/types'
import classNames from 'classnames'
import { vn } from 'vend-number'
import { useSaleManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleLocalId,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { useFormatCurrencyValue } from '../summary-panel/useFormatCurrencyValue'
import { PayLinkLimitModal } from '../../payments/pay-link-limit-modal/PayLinkLimitModal'
import { CashDiscountingBanner } from './CashDiscountingBanner'
import { PaymentOptions } from './PaymentOptions'
import { useFetchPaymentTypes } from './hooks/useFetchPaymentTypes'
import { LSPayButton } from './LSPayButton'
import { AmountTendered } from './AmountTendered/AmountTendered'
import { usePaymentInit } from './hooks/usePaymentInit'
import { CustomerSection } from './CustomerSection'
import { useShowSaveSaleButton } from './hooks/useShowSaveSaleButton'
import { SaveSale } from './SaveSale'
import { CustomerPaymentOptions } from './CustomerPaymentOptions'
import { CustomerPaymentTypeMessage } from './CustomerPaymentTypeMessage/CustomerPaymentTypeMessage'
import { usePreventPageReload } from './hooks/usePreventPageReload'
import { useHandlePaymentType } from './hooks/useHandlePayment'
import { PaymentModal } from './PaymentModal/PaymentModal'
import { usePaymentDisabledState } from './hooks/usePaymentDisabledState'
import { useLaybyOnAccountSale } from './hooks/useLaybyOnAccountSale'

export const PaymentPanel = () => {
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const saleManager = useSaleManager()

  const formatCurrencyValue = useFormatCurrencyValue()

  const cashDiscountingOutstandingBalance = saleManager.getCashDiscountedOutstandingBalance()
  const {
    filteredPaymentTypes,
    systemPaymentTypes,
    fetchPaymentTypes,
  } = useFetchPaymentTypes()
  const { allowPageReload, preventPageReload } = usePreventPageReload()
  const localId = useAppSelector(selectSaleLocalId)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const [amountTendered, setAmountTendered] = useState(
    outstandingBalance.toString()
  )

  const [showSaveSaleButton] = useShowSaveSaleButton()
  const {
    isLoyaltyPaymentDisabled,
    isStoreCreditPaymentDisabled,
  } = usePaymentDisabledState(amountTendered)

  /**
   * need to update amount tendered when:
   *  - customer removed, customer changed, or when account balance payment added but not paid
   * set amountTendered to certain value if provided
   * otherwise set it to the sale outstanding balance
   */
  const updateAmountTendered = (amount?: number | string) => {
    if (amount) {
      setAmountTendered(amount.toString())
    } else {
      setAmountTendered(outstandingBalance.toString())
    }
  }

  // register & deregister init events once when component mount / unmount
  usePaymentInit({ fetchPaymentTypes, updateAmountTendered })

  const [hasPaymentAmountError, setHasPaymentAmountError] = useState(false)

  const {
    handlePayment,
    handleCompletePayment,
    selectedPaymentType,
    clearSelectedPaymentType,
  } = useHandlePaymentType(
    amountTendered,
    paymentProcessing,
    hasPaymentAmountError,
    setPaymentProcessing,
    allowPageReload,
    updateAmountTendered
  )

  const pay = (paymentType: ApiRetailerPaymentType) => {
    preventPageReload()
    handlePayment(paymentType)
  }

  const handleCustomerSelect = (customer?: ApiCustomer) => {
    if (!customer) {
      return
    }
    saleManager.addCustomer(customer).then(() => {
      // TODO once other components have been added
      // .then(() => this.buildPaymentOptions())
      // .then(() => this.resolveCustomerGroup(this.sale.customer!))
      // .then(() => this.setTenderedAmount())
      updateAmountTendered()
    })
  }

  const {
    laybyOnAccountSale,
    isPayLinkModalOpen,
    handlePayLinkLimitModalClose,
  } = useLaybyOnAccountSale(
    localId,
    amountTendered,
    paymentProcessing,
    setPaymentProcessing
  )

  return (
    <>
      <Card className="payment-panel--action">
        <div
          className={classNames(
            'vd-flex vd-flex--column payment-panel-content--action',
            {
              'pro-payments-ready': !paymentProcessing,
            }
          )}
        >
          {saleManager.hasCashDiscountingServiceFee() &&
          cashDiscountingOutstandingBalance ? (
            <CashDiscountingBanner
              balance={formatCurrencyValue(cashDiscountingOutstandingBalance)}
            />
          ) : null}
          <AmountTendered
            amountTendered={amountTendered}
            onChange={setAmountTendered}
            setHasError={setHasPaymentAmountError}
          />
          <LSPayButton paymentType={systemPaymentTypes.lspayPaymentType} />
          <PaymentOptions
            pay={pay}
            amountTendered={amountTendered}
            hasValidTenderedAmount={!hasPaymentAmountError}
            filteredPaymentTypes={filteredPaymentTypes}
            systemPaymentTypes={systemPaymentTypes}
          />
          <CustomerSection
            onSelect={handleCustomerSelect}
            canAddAccountBalancePayment={true}
          />
          <CustomerPaymentOptions
            systemPaymentTypes={systemPaymentTypes}
            pay={pay}
            laybyOnAccountSale={laybyOnAccountSale}
            isLoyaltyPaymentDisabled={isLoyaltyPaymentDisabled}
            isStoreCreditPaymentDisabled={isStoreCreditPaymentDisabled}
          />
          <CustomerPaymentTypeMessage amountTendered={amountTendered} />
        </div>
        {showSaveSaleButton && (
          <div className="sale-action-bar save-sale">
            <SaveSale />
          </div>
        )}
      </Card>
      {selectedPaymentType && (
        <PaymentModal
          paymentOptions={selectedPaymentType.paymentOptions}
          onClose={clearSelectedPaymentType}
          onSuccess={handleCompletePayment}
        />
      )}
      {isPayLinkModalOpen && (
        <PayLinkLimitModal
          amount={vn(amountTendered).toNumber()}
          close={handlePayLinkLimitModalClose}
        />
      )}
    </>
  )
}

export const ReactPaymentPanel = reactular(PaymentPanel, [], 'reactWrapper')
